import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Typography, Box, Paper, Chip } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import Loader from 'components/shared/Loader'
import ReportEmptyState from 'components/shared/ReportEmptyState'
import strings from 'strings'
import { getDscr } from '../../../../../actions/applicants'
import { ACCENT_PURPLE, BANNER_BG } from '../IncomeStatement/incomeStatementAdapters'
import {
  deriveComponentRows, deriveTiles, formatOptionalCurrency, formatPeriodRange, formatRatio, headlineText, isReported, readResult,
  shownRatio, statusText, statusTone, TONE_COLORS
} from './dscrAdapters'
import { reportStyles } from '../shared/reportStyles'

const useStyles = makeStyles((theme) => ({
  ...reportStyles(theme),
  banner: { backgroundColor: BANNER_BG, borderRadius: '14px', padding: theme.spacing(4), color: '#fff', display: 'flex', justifyContent: 'space-between', gap: theme.spacing(3), [theme.breakpoints.down('xs')]: { flexDirection: 'column' } },
  bannerEyebrow: { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', marginBottom: theme.spacing(1), display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  bannerListHead: { fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: ACCENT_PURPLE, marginTop: theme.spacing(2), marginBottom: 4 },
  bannerList: { margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, lineHeight: 1.5, color: 'rgba(255,255,255,0.7)', maxWidth: 820 },
  bannerItem: { display: 'flex', gap: 10, alignItems: 'flex-start' },
  bannerDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: ACCENT_PURPLE, flexShrink: 0, marginTop: 7 },
  statusChip: { fontSize: 10, fontWeight: 700, height: 20, color: '#fff' },
  tilesRow: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(2) },
  tileValue: { fontSize: 24, fontWeight: 700, textAlign: 'center', margin: '8px 0' },
  tileNote: { fontSize: 11, color: '#777', textAlign: 'center' },
  twoColRow: { display: 'flex', gap: theme.spacing(3), flexWrap: 'wrap' },
  colWide: { flex: '1.4 1 480px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: theme.spacing(3) },
  colNarrow: { flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: theme.spacing(3) },
  sectionCard: { padding: theme.spacing(3), borderRadius: '12px', border: '1px solid #ECECEC', boxShadow: 'none' },
  sectionTitle: { fontWeight: 700, fontSize: 15 },
  sectionSubtitle: { fontSize: 12, color: '#777' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 8 },
  th: { textAlign: 'left', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--neutral-400)', padding: '6px 8px', borderBottom: '1px solid #ECECEC' },
  td: { padding: '8px 8px', borderBottom: '1px solid #F1F1F1', fontSize: 13, verticalAlign: 'top' },
  right: { textAlign: 'right', whiteSpace: 'nowrap' },
  excluded: { color: '#999', textDecoration: 'line-through' },
  muted: { color: '#999', fontSize: 11, display: 'block' },
  capacityValue: { fontSize: 22, fontWeight: 700, margin: '6px 0 2px' },
  docRow: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid #F1F1F1', fontSize: 13 },
  footnote: { fontSize: 11, color: '#999', marginTop: theme.spacing(1) },
}))

const Dscr = ({ applicationId }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [raw, setRaw] = useState(null)

  useEffect(() => {
    if (!applicationId) {
      setLoading(false)
      return
    }
    setLoading(true)
    getDscr(applicationId)()
      .then((res) => { setRaw(res); setError(null) })
      .catch((err) => setError(err && err.status === 403 ? strings.DSCR_FORBIDDEN : strings.DSCR_LOAD_ERROR))
      .finally(() => setLoading(false))
  }, [applicationId])

  const result = useMemo(() => readResult(raw), [raw])
  const tiles = useMemo(() => deriveTiles(result), [result])
  const rows = useMemo(() => deriveComponentRows(result), [result])
  const ratio = shownRatio(result)
  const tone = statusTone(result)

  if (loading) return <Box className={classes.container}><Loader /></Box>
  if (error) return <Box className={classes.container}><Alert severity="error">{error}</Alert></Box>
  if (!result) {
    return <Box className={classes.container}><ReportEmptyState title={strings.DSCR_EMPTY_TITLE} description={strings.DSCR_EMPTY_MESSAGE} /></Box>
  }

  const noDocuments = result.status === 'MISSING_DOCUMENTS' && !result.ebitda && !result.principalPayments
  if (noDocuments) {
    return <Box className={classes.container}><ReportEmptyState title={strings.DSCR_EMPTY_TITLE} description={strings.DSCR_EMPTY_MESSAGE} /></Box>
  }

  const documents = result.documents || {}
  const capacity = result.capacity
  const history = result.history || []

  return (
    <Box className={classes.container}>
      <Box className={classes.layout}>
        <Box className={classes.banner} data-testid="dscr-banner">
          <Box>
            <Typography className={classes.bannerEyebrow} component="div">
              <span>{strings.DSCR_EYEBROW}</span>
              <Chip size="small" label={statusText(result.status)} className={classes.statusChip} style={{ backgroundColor: TONE_COLORS[tone] }} />
              {result.periodLabel && <span style={{ color: 'rgba(255,255,255,0.6)' }}>{strings.DSCR_PERIOD_PREFIX} {formatPeriodRange(result.periodStart, result.periodEnd)}</span>}
            </Typography>
            <Typography className={classes.bannerHeadline}>{headlineText(result)}</Typography>
            <Typography className={classes.bannerDescription}>{strings.DSCR_FORMULA}</Typography>
            {isReported(result.interestCoverage) && (
              <Typography className={classes.bannerDescription} style={{ marginTop: 8 }}>
                {strings.DSCR_INTEREST_COVERAGE}: <strong>{formatRatio(result.interestCoverage)}</strong> · {strings.DSCR_INTEREST_COVERAGE_NOTE}
              </Typography>
            )}
            {result.missing && result.missing.length > 0 && (
              <>
                <Typography className={classes.bannerListHead}>{strings.DSCR_MISSING_HEAD}</Typography>
                <ul className={classes.bannerList}>
                  {result.missing.map((m) => <li key={m} className={classes.bannerItem}><span className={classes.bannerDot} />{m}</li>)}
                </ul>
              </>
            )}
            {result.warnings && result.warnings.length > 0 && (
              <>
                <Typography className={classes.bannerListHead}>{strings.DSCR_WARNINGS_HEAD}</Typography>
                <ul className={classes.bannerList}>
                  {result.warnings.map((w) => <li key={w} className={classes.bannerItem}><span className={classes.bannerDot} />{w}</li>)}
                </ul>
              </>
            )}
          </Box>
          <Box className={classes.bannerScoreCol}>
            <Typography className={classes.bannerScoreLabel}>{ratio.indicative ? strings.DSCR_INDICATIVE_LABEL : strings.DSCR_SCORE_LABEL}</Typography>
            <Typography className={classes.bannerScoreValue} data-testid="dscr-value">{formatRatio(ratio.value)}</Typography>
            {isReported(result.targetDscr) && <Typography className={classes.bannerTrend}>{strings.DSCR_TARGET_PREFIX} {formatRatio(result.targetDscr)}</Typography>}
          </Box>
        </Box>

        <Box className={classes.tilesRow}>
          {tiles.map((tile) => (
            <Paper key={tile.key} className={classes.tile}>
              <Typography className={classes.tileLabel}>{tile.name}</Typography>
              <Typography className={classes.tileValue} style={tile.negative ? { color: TONE_COLORS.High } : undefined}>{tile.value}</Typography>
              <Typography className={classes.tileNote}>{tile.sub}</Typography>
            </Paper>
          ))}
        </Box>

        <Box className={classes.twoColRow}>
          <Box className={classes.colWide}>
            <Paper className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>{strings.DSCR_COMPONENTS_TITLE}</Typography>
              <Typography className={classes.sectionSubtitle}>{strings.DSCR_COMPONENTS_SUBTITLE}</Typography>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>{strings.DSCR_COL_COMPONENT}</th>
                    <th className={classes.th}>{strings.DSCR_COL_SOURCE}</th>
                    <th className={`${classes.th} ${classes.right}`}>{strings.DSCR_COL_AMOUNT}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.key}>
                      <td className={classes.td}>
                        {row.name}
                        {row.note && <span className={classes.muted}>{row.note}</span>}
                      </td>
                      <td className={classes.td}>{row.source}</td>
                      <td className={`${classes.td} ${classes.right} ${row.excluded ? classes.excluded : ''}`}>{formatOptionalCurrency(row.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Typography className={classes.footnote}>{strings.DSCR_NOTE_CALCULATION}</Typography>
            </Paper>

            {history.length > 1 && (
              <Paper className={classes.sectionCard}>
                <Typography className={classes.sectionTitle}>{strings.DSCR_HISTORY_TITLE}</Typography>
                <Typography className={classes.sectionSubtitle}>{strings.DSCR_HISTORY_SUBTITLE}</Typography>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th className={classes.th}>{strings.DSCR_COL_PERIOD}</th>
                      <th className={classes.th}>{strings.DSCR_COL_STATUS}</th>
                      <th className={`${classes.th} ${classes.right}`}>{strings.DSCR_KPI_EBITDA}</th>
                      <th className={`${classes.th} ${classes.right}`}>{strings.DSCR_KPI_DEBT_SERVICE}</th>
                      <th className={`${classes.th} ${classes.right}`}>{strings.DSCR_COL_DSCR}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((entry) => (
                      <tr key={entry.periodLabel}>
                        <td className={classes.td}>{formatPeriodRange(entry.periodStart, entry.periodEnd)}</td>
                        <td className={classes.td}>{statusText(entry.status)}</td>
                        <td className={`${classes.td} ${classes.right}`}>{formatOptionalCurrency(entry.ebitda)}</td>
                        <td className={`${classes.td} ${classes.right}`}>{formatOptionalCurrency(entry.totalIdentifiedDebtService)}</td>
                        <td className={`${classes.td} ${classes.right}`}>{formatRatio(isReported(entry.dscr) ? entry.dscr : entry.indicativeDscr)}{!isReported(entry.dscr) && isReported(entry.indicativeDscr) ? ' *' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Paper>
            )}
          </Box>

          <Box className={classes.colNarrow}>
            <Paper className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>{strings.DSCR_CAPACITY_TITLE}</Typography>
              <Typography className={classes.sectionSubtitle}>{strings.DSCR_CAPACITY_SUBTITLE}</Typography>
              {capacity ? (
                <>
                  <Typography className={classes.tileLabel} style={{ marginTop: 12 }}>{strings.DSCR_CAPACITY_MAX_SERVICE}</Typography>
                  <Typography className={classes.capacityValue}>{formatOptionalCurrency(capacity.maxDebtService)}</Typography>
                  <Typography className={classes.tileLabel} style={{ marginTop: 12 }}>{strings.DSCR_CAPACITY_MAX_PRINCIPAL}</Typography>
                  <Typography className={classes.capacityValue}>{formatOptionalCurrency(capacity.maxPrincipal)}</Typography>
                </>
              ) : (
                <Typography className={classes.footnote}>{strings.DSCR_CAPACITY_UNAVAILABLE}</Typography>
              )}
            </Paper>

            <Paper className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>{strings.DSCR_DOCUMENTS_TITLE}</Typography>
              <Box className={classes.docRow}>
                <span>{strings.DSCR_DOC_INCOME}</span>
                <span style={{ textAlign: 'right' }}>
                  {documents.incomeStatementPeriod || strings.DSCR_DOC_MISSING}
                  {documents.incomeStatementConfidence && <span className={classes.muted}>{strings.DSCR_DOC_CONFIDENCE} {documents.incomeStatementConfidence}</span>}
                </span>
              </Box>
              <Box className={classes.docRow} style={{ borderBottom: 'none' }}>
                <span>{strings.DSCR_DOC_CASH_FLOW}</span>
                <span style={{ textAlign: 'right' }}>
                  {documents.cashFlowPeriod || strings.DSCR_DOC_MISSING}
                  {documents.cashFlowConfidence && <span className={classes.muted}>{strings.DSCR_DOC_CONFIDENCE} {documents.cashFlowConfidence}</span>}
                </span>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

Dscr.propTypes = { applicationId: PropTypes.string }

export default Dscr
